import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { HistoryOutput } from 'src/app/models/history.model';
import {
  UploadFileInfo,
  UploadFileType,
} from 'src/app/models/uploadFileInfo.model';
import { BuildingService } from 'src/app/services/building.service';
import { CommonService } from 'src/app/services/common.service';
import { CustomerService } from 'src/app/services/customer.service';
import { DtService } from 'src/app/services/dt.service';
import { PoleService } from 'src/app/services/pole.service';
import { MeterService } from 'src/app/services/meter.service';

import { UploadFileInfoService } from 'src/app/services/uploadFileInfo.service';
import { SurveyService } from 'src/app/services/survey.service';
import { UserService } from 'src/app/services/user.service';
import { PermissionsEnum } from 'src/app/models/role.model';
import { PermissionsService } from 'shared-lib';
import { MeterManagementService } from 'src/app/services/meter-management.service';
import { BillingAccountService } from 'src/app/services/billing-account.service';
import { SubstationService } from 'src/app/services/substation.service';
import { AccountKycService } from 'src/app/services/kyc/account-kyc.service';
import { InstallationService } from 'src/app/services/installation.service';
import { HistoryComponent } from '../history/history.component';
import { MeterInventoryType } from 'src/app/enums/meter-inventory-type.enum';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
})
export class UploadComponent implements OnInit {
  uploads: UploadFileInfo[];
  uploadType: string = '';

  uploadFile: File | null;
  file: string | ArrayBuffer;
  action: string = '';
  pageSize: number = 3;
  currentPage: number = 1;
  count: number = 2;
  utilityId: number = 0;
  meterInventoryType: MeterInventoryType = MeterInventoryType.Customer;

  description: string = '';
  serviceUploadOrder: string = '';

  openMenuUploadId: number | null = null;

  @ViewChild('openUploadHistoryModal')
  openUploadHistoryModal: ElementRef<HTMLButtonElement>;

  @ViewChild('historyModal') historyModal: HistoryComponent;

  constructor(
    private customersService: CustomerService,
    private toastr: ToastrService,
    private commonService: CommonService,
    private uploadFileService: UploadFileInfoService,
    private route: ActivatedRoute,
    private buildingService: BuildingService,
    private surveyService: SurveyService,
    private poleService: PoleService,
    private dtService: DtService,
    private userService: UserService,
    private meterService: MeterService,
    private permissionsService: PermissionsService,
    private meterStockService: MeterManagementService,
    private billingAccountService: BillingAccountService,
    private substationService: SubstationService,
    private router: Router,
    private accountKycService: AccountKycService,
    private installationService: InstallationService,
  ) { }

  ngOnInit(): void {
    this.uploadType = this.route.snapshot.paramMap.get('type') || '';
    if (
      (this.uploadType === '8' &&
        !this.permissionsService.isUserManagementBulkAssignAllowed) ||
      (this.uploadType === '9' &&
        !this.permissionsService.isUserManagementCreateEditAllowed)
    ) {
      this.router.navigate(['']);
      this.toastr.error("You don't have permission");
    }
    this.utilityId = parseInt(
      this.route.snapshot.paramMap.get('utilityId') || '',
    );
    this.meterInventoryType =
      parseInt(this.route.snapshot.queryParamMap.get('type') || '') ||
      MeterInventoryType.Customer;
    this.reloadTable();
  }

  fileBrowserHandler($event: any) {
    this.uploadFile = $event.target.files.item(0);
    const reader = new FileReader();
    this.file = this.uploadFile!.name;
    reader.readAsDataURL(this.uploadFile!);

    $event.target.value = '';

    this.promptForUploadMetadata();
  }

  onFileDropped($event: any) {
    this.uploadFile = $event.item(0);
    const reader = new FileReader();

    this.file = this.uploadFile!.name;

    reader.readAsDataURL(this.uploadFile!);
    this.promptForUploadMetadata();
  }

  private promptForUploadMetadata() {
    this.description = '';
    this.serviceUploadOrder = '';
    this.historyModal?.reset();
    this.openUploadHistoryModal?.nativeElement?.click();
  }

  onUploadMetaSaved(history: HistoryOutput) {
    this.description = history.UpdateDescription ?? '';
    this.serviceUploadOrder = history.ServiceOrderUpload ?? '';

    const closeBtn = document.querySelector(
      '#history .btn-cancel-modal',
    ) as HTMLElement | null;
    closeBtn?.click();

    this.handleUploadForm();
  }

  handleUploadForm() {
    if (!this.uploadFile) {
      return;
    }
    let data = new FormData();
    data.append('file', this.uploadFile!, this.uploadFile?.name);
    data.append('description', this.description);
    data.append('serviceUploadOrder', this.serviceUploadOrder);

    switch (this.uploadType) {
      case '1':
        this.uploadBuildingsFile(data);
        break;
      case '2':
        this.uploadCustomersFile(data);
        break;
      case '3':
        this.uploadPolesFile(data);
        break;
      case '4':
        this.uploadDTFile(data);
        break;
      case '5':
        this.uploadMetersFile(data);
        break;
      case '6':
        this.uploadSurveysFile(data);
        break;
      case '7':
        this.uploadDTCrudFile(data);
        break;
      case '8':
        this.usersUploadTemplate(data, true);
        break;
      case '9':
        this.usersUploadTemplate(data, false);
        break;
      case '10':
        this.uploadMetersStockFile(data);
        break;
      case '11':
        this.uploadRegularizationFile(data);
        break;
      case '021':
        this.uploadBillAccounts(data);
        break;
      case '22':
        this.uploadConvertedCustomers(data);
        break;
      case '23':
        this.uploadUpdatedCustomers(data);
        break;
      case '24':
        this.uploadFlagMetersForMeterValidation(data);
        break;
      case '25':
        this.reassignDts(data);
        break;
      case '26':
        this.uploadMeterConfiguration(data);
        break;
      case '31':
        this.uploadSubstationFile(data);
        break;
      case '32':
        this.uploadNewConnectionFile(data);
        break;
      case '33':
        this.uploadBulkVendorAssignmentFile(data);
            break;
      case '34':
        this.uploadBulkConfirmPaymentFile(data);
        break;
      case '35':
          this.uploadUpdateCustomersFile(data);
          break;
      case '36':
        this.uploadInstallationBulkAssignment(data);
        break;
      case '37':
        this.uploadBulkVendorReassignmentFile(data);
        break;
      case '38':
        this.bulkOrgAssignment(data);
        break;
      case '39':
        this.bulkRoleReassignment(data);
        break;
      case '40':
        this.uploadAuthorizeMetering(data);
        break;
      case '41':
        this.uploadBulkDtVendorAssignment(data);
        break;
      case '42':
        this.uploadBulkDtInstallerAssignment(data);
        break;
      case '43':
        this.uploadBulkDtVendorReassignment(data);
        break;
      default:
        break;
    }
  }

  bulkRoleReassignment(data: FormData) {
    this.userService.bulkRoleReassignment(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  bulkOrgAssignment(data: FormData) {
    this.userService.bulkOrgAssignment(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }
  uploadUpdateCustomersFile(data: FormData) {
    this.customersService
      .uploadFileForCustomerUpdate(data, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadMeterConfiguration(data: FormData) {
    this.meterService
      .uploadMeterConfigurationFile(data, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadFlagMetersForMeterValidation(data: FormData) {
    this.customersService
      .uploadFileFlagMetersForMeterValidaiton(data, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadUpdatedCustomers(data: FormData) {
    let isNewConnection =
      this.route.snapshot.paramMap.get('isNewConnection') || '';

    this.customersService
      .uploadFileForUpdate(data, isNewConnection, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadConvertedCustomers(data: FormData) {
    let isNewConnection =
      this.route.snapshot.paramMap.get('isNewConnection') || '';

    this.customersService
      .uploadFileForConvert(data, isNewConnection, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadBillAccounts(data: FormData) {
    this.billingAccountService.uploadFile(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }
  uploadMetersStockFile(data: FormData) {
    this.meterStockService.uploadFile(data, this.utilityId, this.meterInventoryType).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0) {
            window.location.href = response.data;
          }
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  uploadDTFile(data: FormData) {
    this.dtService.uploadFile(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  uploadDTCrudFile(data: FormData) {
    this.dtService.uploadFile(data, this.utilityId, 'true').subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  uploadSubstationFile(data: FormData) {
    this.substationService.uploadFile(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  uploadNewConnectionFile(data: FormData) {
    this.accountKycService.uploadFile(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }
  usersUploadTemplate(data: FormData, isBulkReassign: boolean) {
    this.userService.uploadFile(data, this.utilityId, isBulkReassign).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  reassignDts(data: FormData) {
    this.userService.reassignDts(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  uploadCustomersFile(data: FormData) {
    let isNewConnection =
      this.route.snapshot.paramMap.get('isNewConnection') || '';
    this.customersService
      .uploadFile(data, isNewConnection, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadRegularizationFile(data: FormData) {
    this.customersService
      .uploadRegularizationFile(data, this.utilityId)
      .subscribe({
        next: (response: any) => {
          if (response.status === 'OK') {
            this.toastr.success(response.message);
          } else if (
            response.status === 'Pending' ||
            response.status === '600'
          ) {
            this.toastr.info(response.message);
            if (response.data && response.data.path)
              window.location.href = response.data.path;
          } else {
            this.toastr.error(response.message);
            if (response.data && response.data.length > 0)
              window.location.href = response.data;
          }

          this.reloadTable();
          this.uploadFile = null;
          this.file = '';
        },
        error: (error) => console.log(error),
      });
  }

  uploadPolesFile(data: FormData) {
    this.poleService.uploadFile(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0)
            window.location.href = response.data;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  uploadBuildingsFile(data: FormData) {
    this.buildingService.uploadFile(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0) {
            window.location.href = response.data;
          }
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }
  uploadMetersFile(data: FormData) {
    this.meterService.uploadFile(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0) {
            window.location.href = response.data;
          }
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }

  uploadSurveysFile(data: FormData) {
    this.surveyService.uploadFile(data, this.utilityId).subscribe(
      (response) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.length > 0) {
            window.location.href = response.data;
          }
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      (error) => {
        this.toastr.error('An error occurred.');
      },
    );
  }
  generateTemplate() {
    switch (this.uploadType) {
      case '1':
        this.getBuildingsTemplate();
        break;
      case '2':
        this.getCustomersTemplate();
        break;
      case '3':
        this.getPolesTemplate();
        break;
      case '4':
        this.getDtTemplate();
        break;
      case '5':
        this.getMetersTemplate();
        break;
      case '6':
        this.getSurveysTemplate();
        break;
      case '7':
        this.getDtCrudTemplate();
        break;
      case '8':
        this.getUsersUploadTemplate(true, false);
        break;
      case '9':
        this.getUsersUploadTemplate(false, false);
        break;
      case '10':
        this.getMeterStockUploadTemplate();
        break;
      case '11':
        this.getRegularizationTemplate();
        break;
      case '021':
        this.getBillingAccountTemplate();
        break;
      case '22':
        this.getConvertedCustomersUploadTemplate();
        break;
      case '23':
        this.getUpdatedCustomersUploadTemplate();
        break;
      case '24':
        this.getFlagMetersForMeterValidationTemplate();
        break;
      case '25':
        this.getUsersUploadTemplate(false, true);
        break;
      case '31':
        this.getSubstationsTemplate();
        break;
      case '32':
        this.getNewConnectionTemplate();
        break;
      case '26':
        this.getMeterConfigurationTemplate();
        break;
      case '32':
        this.getNewConnectionTemplate();
        break;
      case '33':
        this.getBulkVendorAssignmentTemplate();
            break;
      case '34':
        this.getBulkConfirmPaymentTemplate();
        break;
      case '35':
        this.generateUpdateUploadTemplate();
        break;
      case '36':
        this.getInstallationBulkAssignmentTemplate();
        break;
      case '37':
        this.getBulkVendorReassignmentTemplate();
        break;
      case '38':
        this.getUsersUploadTemplate(false, false, true);
        break;
      case '39':
        this.getUsersUploadTemplate(false, false, false, true);
        break;
      case '40':
        this.getAuthorizeMeteringTemplate();
        break;
      case '41':
        this.getBulkDtVendorAssignmentTemplate();
        break;
      case '42':
        this.getBulkDtInstallerAssignmentTemplate();
        break;
      case '43':
        this.getBulkDtVendorReassignmentTemplate();
        break;
    }
  }
  getUpdatedCustomersUploadTemplate() {
    this.customersService.generateTemplateForUpdate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getFlagMetersForMeterValidationTemplate() {
    this.customersService
      .getnerateTemplateFlagMetersForMeterValidation(this.utilityId)
      .subscribe({
        next: (data) => {
          if (data?.status === 'OK' || data?.status === '200') {
            window.location.href = data.data;
          } else this.toastr.error(data?.message || 'Request failed');
        },
        error: (error) => console.log(error),
      });
  }

  getConvertedCustomersUploadTemplate() {
    this.customersService.generateTemplateForConvert(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getMeterStockUploadTemplate() {
    this.meterStockService.generateTemplate(this.utilityId, this.meterInventoryType).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getUsersUploadTemplate(isBulkReassign: boolean, isDtReassign: boolean, isBulkOrgAssignment: boolean = false, isBulkRoleReassignment: boolean = false) {
    this.userService
      .generateTemplate(this.utilityId, isBulkReassign, isDtReassign, isBulkOrgAssignment, isBulkRoleReassignment)
      .subscribe({
        next: (data) => {
          if (data?.status === 'OK' || data?.status === '200') {
            window.location.href = data.data;
          } else this.toastr.error(data?.message || 'Request failed');
        },
        error: (error) => console.log(error),
      });
  }
  getBillingAccountTemplate() {
    this.billingAccountService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getDtTemplate() {
    this.dtService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getDtCrudTemplate() {
    this.dtService.generateTemplate(this.utilityId, 'true').subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getCustomersTemplate() {
    this.customersService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  generateUpdateUploadTemplate() {
    this.customersService.generateUpdateUploadTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  getRegularizationTemplate() {
    this.customersService
      .generateRegularizationTemplate(this.utilityId)
      .subscribe({
        next: (data) => {
          if (data?.status === 'OK' || data?.status === '200') {
            window.location.href = data.data;
          } else this.toastr.error(data?.message || 'Request failed');
        },
        error: (error) => console.log(error),
      });
  }
  getBuildingsTemplate() {
    this.buildingService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getPolesTemplate() {
    this.poleService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getSurveysTemplate() {
    this.surveyService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getMetersTemplate() {
    this.meterService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getMeterConfigurationTemplate() {
    this.meterService
      .generateMeterConfigurationTemplate(this.utilityId)
      .subscribe({
        next: (data) => {
          if (data?.status === 'OK' || data?.status === '200') {
            window.location.href = data.data;
          } else this.toastr.error(data?.message || 'Request failed');
        },
        error: (error) => console.log(error),
      });
  }

  getSubstationsTemplate() {
    this.substationService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }
  getNewConnectionTemplate() {
    this.accountKycService.generateTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkVendorAssignmentFile(data: FormData) {
    this.surveyService.uploadBulkVendorAssignment(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  getBulkVendorAssignmentTemplate() {
    this.accountKycService.generateBulkVendorAssignmentTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getBulkConfirmPaymentTemplate() {
    this.accountKycService.generateBulkConfirmPaymentTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkVendorReassignmentFile(data: FormData) {
    this.surveyService.uploadBulkVendorReassignment(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  getBulkVendorReassignmentTemplate() {
    this.surveyService.generateBulkVendorReassignmentTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkConfirmPaymentFile(data: FormData) {
    this.accountKycService.uploadBulkConfirmPayment(data, this.utilityId).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }
  uploadAuthorizeMetering(data: FormData) {
    this.substationService.uploadAuthorizeMetering(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  uploadInstallationBulkAssignment(data: FormData) {
    this.installationService.uploadBulkAssignment(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  getInstallationBulkAssignmentTemplate() {
    this.installationService.generateBulkAssignmentTemplate().subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkDtVendorAssignment(data: FormData) {
    this.installationService.uploadBulkDtVendorAssignment(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkDtInstallerAssignment(data: FormData) {
    this.installationService.uploadBulkDtInstallerAssignment(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  getBulkDtVendorAssignmentTemplate() {
    this.installationService.generateBulkDtVendorAssignmentTemplate().subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  uploadBulkDtVendorReassignment(data: FormData) {
    this.installationService.uploadBulkDtVendorReassignment(data).subscribe({
      next: (response: any) => {
        if (response.status === 'OK') {
          this.toastr.success(response.message);
        } else if (response.status === 'Pending' || response.status === '600') {
          this.toastr.info(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        } else {
          this.toastr.error(response.message);
          if (response.data && response.data.path)
            window.location.href = response.data.path;
        }

        this.reloadTable();
        this.uploadFile = null;
        this.file = '';
      },
      error: (error) => console.log(error),
    });
  }

  getBulkDtVendorReassignmentTemplate() {
    this.installationService.generateBulkDtVendorReassignmentTemplate().subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getBulkDtInstallerAssignmentTemplate() {
    this.installationService.generateBulkDtInstallerAssignmentTemplate().subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getAuthorizeMeteringTemplate() {
    this.substationService.generateAuthorizeMeteringTemplate(this.utilityId).subscribe({
      next: (data) => {
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  getByType(obj: any) {
    this.uploadFileService.getByType(obj).subscribe({
      next: (response) => {
        if (response.status === 'OK' || response.status === '200') {
          this.uploads = response.data.data;
          this.count = response.data.count;
        } else {
          this.toastr.error(response.message);
        }
      },
    });
  }

  get isDownloadMenuEnabled(): boolean {
    return this.uploadType === '2' || this.uploadType === '35';
  }

  toggleDownloadMenu(id: number, event: MouseEvent) {
    event.stopPropagation();
    this.openMenuUploadId = this.openMenuUploadId === id ? null : id;
  }

  @HostListener('document:click')
  closeDownloadMenu() {
    this.openMenuUploadId = null;
  }

  downloadUploadedFile(upload: UploadFileInfo) {
    if (!upload.uploadedFilePath) return;
    window.location.href = upload.uploadedFilePath;
    this.openMenuUploadId = null;
  }

  downloadValidationErrorFile(upload: UploadFileInfo) {
    if (!upload.errorFilePath) return;
    window.location.href = upload.errorFilePath;
    this.openMenuUploadId = null;
  }

  getUploadStatusColor(status: string) {
    if (status === 'Completed') {
      return 'rgba(0, 136, 90, 1)';
    } else if (status == 'Processing') {
      return 'rgba(251, 159, 48, 1)';
    } else {
      return 'rgba(0, 0, 0, 1)';
    }
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  reloadTable(page: any = null) {
    if (page) this.currentPage = page;
    var uploadFileType = UploadFileType.Customers;
    switch (this.uploadType) {
      case '1':
        uploadFileType = UploadFileType.Buildings;
        break;
      case '3':
        uploadFileType = UploadFileType.Poles;
        break;
      case '4':
        uploadFileType = UploadFileType.Dts;
        break;
      case '5':
        uploadFileType = UploadFileType.Meters;
        break;
      case '6':
        uploadFileType = UploadFileType.Surveys;
        break;
      case '7':
        uploadFileType = UploadFileType.DtsCrud;
        break;
      case '8':
        uploadFileType = UploadFileType.BulkReassign;
        break;
      case '9':
        uploadFileType = UploadFileType.BulkUserCreation;
        break;
      case '10':
        uploadFileType = UploadFileType.MetersStock;
        break;
      case '11':
        uploadFileType = UploadFileType.Regularization;
        break;
      case '021':
        uploadFileType = UploadFileType.BillingAccount;
        break;
      case '22':
        uploadFileType = UploadFileType.CustomersConvert;
        break;
      case '23':
        uploadFileType = UploadFileType.CustomersUpdate;
        break;
      case '24':
        uploadFileType = UploadFileType.FlagMetersForMeterValidation;
        break;
      case '25':
        uploadFileType = UploadFileType.ReassignDts;
        break;
      case '26':
        uploadFileType = UploadFileType.MeterConfiguration;
        break;
      case '31':
        uploadFileType = UploadFileType.Substations;
        break;
      case '32':
        uploadFileType = UploadFileType.NewConnection;
        break;
      case '33':
        uploadFileType = UploadFileType.BulkVendorAssignment;
        break;
      case '34':
        uploadFileType = UploadFileType.BulkConfirmPayment;
        break;
      case '35':
          uploadFileType = UploadFileType.UploadCustomerUpdate;
          break;
      case '36':
        uploadFileType = UploadFileType.InstallationBulkAssignment;
        break;
      case '37':
        uploadFileType = UploadFileType.BulkVendorReassignment;
        break;
      case '38':
        uploadFileType = UploadFileType.BulkOrgAssignment;
        break;
      case '39':
        uploadFileType = UploadFileType.BulkRoleReassignment;
        break;
      case '40':
        uploadFileType = UploadFileType.AuthorizeMetering;
        break;
      case '41':
        uploadFileType = UploadFileType.BulkDtVendorAssignment;
        break;
      case '42':
        uploadFileType = UploadFileType.BulkDtInstallerAssignment;
        break;
      case '43':
        uploadFileType = UploadFileType.BulkDtVendorReassignment;
        break;
    }

    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },

      UploadFileType: uploadFileType,
      utilityId: this.utilityId,
    };

    this.getByType(obj);
  }
}
